import React, { useState, useEffect } from "react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Shuffle, Loader2, CheckCircle2, AlertCircle, CircleDashed } from "lucide-react";

const Chips = ({ items, cls }) => (
  <div className="flex flex-wrap gap-1.5 mt-1">{items.map(n => <span key={n} className={`text-[11px] px-2 py-0.5 rounded-full ${cls}`}>{n}</span>)}</div>
);

export const PreferenceSummary = ({ planId, token, refreshKey }) => {
  const [sum, setSum] = useState(null);
  useEffect(() => {
    fetch(`${API}/staff/meal-plans/${planId}/preference-summary`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null).then(setSum).catch(() => {});
  }, [planId, token, refreshKey]);
  if (!sum) return null;
  if (!sum.has_preferences) return (
    <div className="rounded-lg border border-stone-200 bg-stone-50 p-3 text-xs text-stone-500" data-testid="pref-summary-none">
      This patient has not ticked any foods yet, so the standard meals were used.
    </div>
  );
  return (
    <div className="rounded-lg border border-stone-200 bg-[#FFFAF5] p-3 space-y-2.5" data-testid="pref-summary">
      <p className="text-xs font-bold text-stone-800">Patient's food choices in this plan</p>
      {sum.used.length > 0 && <div data-testid="pref-summary-used"><p className="text-[11px] font-semibold text-green-700 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />Used in the plan ({sum.used.length})</p><Chips items={sum.used} cls="bg-green-50 text-green-800 border border-green-200" /></div>}
      {sum.not_in_library.length > 0 && <div data-testid="pref-summary-manual"><p className="text-[11px] font-semibold text-amber-700 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />Add by hand: not in the meal library yet ({sum.not_in_library.length})</p><Chips items={sum.not_in_library} cls="bg-amber-50 text-amber-800 border border-amber-200" /></div>}
      {sum.ticked_not_used.length > 0 && <div data-testid="pref-summary-unused"><p className="text-[11px] font-semibold text-stone-600 flex items-center gap-1"><CircleDashed className="w-3.5 h-3.5" />Ticked but not used yet ({sum.ticked_not_used.length})</p><Chips items={sum.ticked_not_used} cls="bg-white text-stone-600 border border-stone-200" /></div>}
      {sum.meals_to_add.length > 0 && <p className="text-[11px] text-red-600" data-testid="pref-summary-meals-to-add">Meals to add: {sum.meals_to_add.join(", ")}</p>}
    </div>
  );
};

export const MealSwapPicker = ({ planId, token, day, mealType, onSwapped }) => {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState(null);
  const [saving, setSaving] = useState(null);

  const load = () => {
    setOpts(null);
    fetch(`${API}/staff/meal-plans/${planId}/swap-options?day=${day}&meal_type=${mealType}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => setOpts(d.options || [])).catch(() => setOpts([]));
  };

  const choose = async (o, i) => {
    setSaving(i);
    try {
      const items = o.items.map(({ label, ...rest }) => rest);
      const r = await fetch(`${API}/staff/meal-plans/${planId}/edit-meal`, { method: "PUT", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify({ day, meal_type: mealType, items, name: o.name }) });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || "Swap failed."); return; }
      toast.success(d.version ? d.message : `Swapped to ${o.name}.`);
      setOpen(false);
      onSwapped({ name: o.name, items }, d.plan_id);
    } catch { toast.error("Connection error."); } finally { setSaving(null); }
  };

  return (
    <Popover open={open} onOpenChange={v => { setOpen(v); if (v) load(); }}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px] text-[#FF7A00] hover:bg-[#FF7A00]/10" data-testid={`swap-meal-${day}-${mealType}`}>
          <Shuffle className="w-3 h-3 mr-1" />Swap
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-2" align="end" data-testid={`swap-options-${day}-${mealType}`}>
        <p className="text-[11px] text-stone-500 px-1 pb-1.5">Built only from the patient's ticked foods</p>
        {!opts ? <Loader2 className="w-4 h-4 animate-spin text-[#FF7A00] m-2" /> : !opts.length ? (
          <p className="text-xs text-stone-500 p-2" data-testid="swap-no-options">No other meals can be made from the patient's ticked foods. Edit the meal by hand instead.</p>
        ) : opts.map((o, i) => (
          <button key={o.name} onClick={() => choose(o, i)} disabled={saving !== null} className="w-full text-left rounded-md px-2 py-1.5 hover:bg-[#FF7A00]/5 disabled:opacity-60" data-testid={`swap-option-${i}`}>
            <p className="text-xs font-semibold text-stone-800 flex items-center gap-1">{saving === i && <Loader2 className="w-3 h-3 animate-spin" />}{o.name}</p>
            <p className="text-[11px] text-stone-500">{o.items.map(it => `${it.qty} ${it.label}`).join(", ")}</p>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
};
